import desktopImg from '@/public/images/onboarding/desktop-step-2.jpg'
import { followArtist, getFollowedArtists, unfollowArtist } from 'api/artists'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Input } from 'components/common/Input'
import { OnboardingScreen } from 'components/onboarding/OnboardingScreen'
import {
  OnboardingScreenHeader,
  OnboardingScreenTitles,
} from 'components/onboarding/OnboardingScreenHeader'
import { useOnboarding } from 'contexts/hooks/useOnboarding'
import { useUserProfile } from 'hooks'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import {
  Configure,
  useHits,
  useInstantSearchContext,
  useSearchBox,
  useToggleRefinement,
} from 'react-instantsearch'
import Skeleton from 'react-loading-skeleton'
import { useMutation, useQuery } from 'react-query'
import { FilterArtistHitProps } from 'types'
import { range } from 'utils/arrays'
import { ARTWORK_IMG_PLACEHOLDER } from 'utils/image'
import { getArtistUrl } from 'utils/urls'

const InstantSearch = dynamic(
  () => import('components/common/InstantSearch').then(m => m.InstantSearch),
  { ssr: false }
)

export function FollowArtists() {
  const { t } = useTranslation('onboarding')
  const { next } = useOnboarding()

  return (
    <OnboardingScreen desktopImg={desktopImg}>
      <OnboardingScreenHeader step={1}>
        <OnboardingScreenTitles
          title={t('follow_artists.title')}
          description={t('follow_artists.description')}
          className='px-8 pt-6 sm:p-0'
        />
      </OnboardingScreenHeader>

      <div className='flex flex-col overflow-hidden px-8 py-6 pb-10 sm:pb-6 bg-warm-50 h-full flex-1 justify-between space-y-6'>
        <div className='overflow-hidden flex flex-col'>
          <InstantSearch index='filter'>
            <ArtistsSearch />
          </InstantSearch>
        </div>

        <div>
          <div className='sm:hidden w-full'>
            <Button
              style='solid-primary'
              pill
              size='lg'
              onClick={next}
              className='w-full'
            >
              {t('follow_artists.action')}
            </Button>
          </div>
          <div className='hidden sm:block w-full'>
            <Button
              style='solid-primary'
              pill
              size='md'
              onClick={next}
              className='w-full'
            >
              {t('follow_artists.action')}
            </Button>
          </div>
        </div>
      </div>
    </OnboardingScreen>
  )
}

function ArtistsSearch() {
  const { t } = useTranslation('onboarding')
  const { hits } = useHits<FilterArtistHitProps>()
  const { query } = useSearchBox()
  const context = useInstantSearchContext()

  useToggleRefinement({
    attribute: 'entity',
    on: 'artist',
    off: 'artist',
  })

  useToggleRefinement({
    attribute: 'isDeleted',
    on: false,
    off: false,
  })

  return (
    <>
      {!query && (
        <Configure filters='name:"Damien Hirst" OR name:"Banksy" OR name:"Yayoi Kusama" OR name:"Werner Bronkhorst"' />
      )}

      <Input
        type='algolia-search'
        placeholder={t('follow_artists.search_artists')}
        pill
        size='lg'
      />

      <ol className='overflow-y-auto flex-1 h-full space-y-5 mt-6'>
        {hits.map(hit => (
          <li key={hit.objectID}>
            <Hit {...hit} />
          </li>
        ))}

        {context.status === 'loading' &&
          range(0, 4).map(i => (
            <li key={i}>
              <HitSkeleton />
            </li>
          ))}
      </ol>

      {context.status === 'idle' && !hits.length && (
        <p className='flex items-center justify-center text-cool-600 text-sm'>
          No results
        </p>
      )}
    </>
  )
}

function Hit({
  images,
  name,
  nationalities,
  dob,
  objectID,
}: FilterArtistHitProps) {
  const { t } = useTranslation('common')
  const { data: userProfile } = useUserProfile({
    options: { refetchOnMount: false },
  })

  const {
    data: followedArtists,
    refetch: refetchArtists,
    isLoading: isLoadingFollowedArtists,
  } = useQuery({
    queryKey: ['followed-artists-onboarding'],
    queryFn: async () => {
      const { results } = await getFollowedArtists({
        pageSize: '100',
        excludeArtworks: true,
      })
      return results
    },
    refetchOnMount: false,
  })

  const id = Number(objectID.split('_')[1])
  const followedArtist = followedArtists?.find(a => a.artist?.id === id)

  const { mutateAsync: follow, isLoading: isLoadingFollow } = useMutation({
    mutationFn: async () => {
      return followArtist({
        artistId: id,
        newHighestBidEmail: Boolean(userProfile?.followArtistNewHighestBid),
        newLowestAskEmail: Boolean(userProfile?.followArtistNewLowestAsk),
        newReleaseEmail: Boolean(userProfile?.followArtistNewRelease),
        newSaleEmail: Boolean(userProfile?.followArtistNewSale),
        onboardingFlow: true,
      })
    },
    onSuccess: async () => {
      await refetchArtists()
    },
  })

  const { mutateAsync: unfollow, isLoading: isLoadingUnfollow } = useMutation({
    mutationFn: async () => {
      if (!followedArtist) return
      return unfollowArtist(followedArtist?.id)
    },
    onSuccess: async () => {
      await refetchArtists()
    },
  })

  const image =
    images?.xs ||
    images?.thumbnail ||
    images?.small ||
    images?.medium ||
    images?.large ||
    ARTWORK_IMG_PLACEHOLDER

  return (
    <div className='flex items-center justify-between'>
      <a
        href={getArtistUrl(name, id)}
        className='flex items-center space-x-3'
        target='_blank'
      >
        <Image
          src={image}
          alt={name}
          width={48}
          height={48}
          className='rounded-full'
        />

        <div>
          <p className='font-medium'>{name}</p>
          <p className='text-sm text-cool-600'>
            {nationalities?.join(', ')}, b. {dob && new Date(dob).getFullYear()}
          </p>
        </div>
      </a>

      {isLoadingFollowedArtists && (
        <Skeleton className='w-16 h-10 rounded-3xl' containerClassName='flex' />
      )}

      {!isLoadingFollowedArtists && (
        <>
          {!followedArtist && (
            <Button
              style='solid-white'
              size='md'
              pill
              onClick={() => follow()}
              disabled={isLoadingFollow}
            >
              {t('follow')}
            </Button>
          )}

          {followedArtist && (
            <Button
              style='solid-primary'
              size='sm'
              pill
              onClick={() => unfollow()}
              disabled={isLoadingUnfollow}
            >
              <Icon name='check' size={16} />
            </Button>
          )}
        </>
      )}
    </div>
  )
}

function HitSkeleton() {
  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center space-x-3'>
        <Skeleton
          className='w-12 h-12 rounded-full'
          containerClassName='flex'
        />

        <div>
          <Skeleton
            className='w-16 h-5 rounded-3xl'
            containerClassName='flex'
          />
          <Skeleton
            className='w-28 h-5 rounded-3xl'
            containerClassName='flex'
          />
        </div>
      </div>

      <Skeleton className='w-16 h-10 rounded-3xl' containerClassName='flex' />
    </div>
  )
}
